import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, getFullnameAbbr, OptionKey, SelectDropdown, Text } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';

import { useBreadcrumbs } from '../core/hooks';

import { useDependents } from './SpendTracker/hooks';
import SpendTracker from './SpendTracker/SpendTracker';
import useDeductiblesStore from './stores/useDeductibles.store';
// import { useAuthStore } from '../user';

interface Props {
  deductibleId?: string;
  yearId?: string;
}
const Deductibles: React.FC<Props> = ({ deductibleId, yearId }) => {
  const { t } = useTranslation();
  const ME_LABEL = t(' (Me)');

  // const currentUserId = useMemo(
  //   () => currentUser?.employee?.id,
  //   [currentUser],
  // );

  const handleSetState = useDeductiblesStore((state) => state.setState);
  const selectedUserId = useDeductiblesStore((state) => state.state.userId);
  const users = useDependents({
    userId: deductibleId,
  });

  const currentUser = useMemo(
    () => users?.find((user) => user.id === selectedUserId),
    [selectedUserId, users]
  );
  const abbr = useMemo(() => getFullnameAbbr(currentUser?.fullName || ''), [currentUser]);
  const usersOptions = useMemo(() => {
    if (!users?.length) return [];
    return users?.map((user) => ({
      key: user.id,
      value: user.id,
      title: user.isMe ? `${user.fullName}${ME_LABEL}` : user.fullName,
    }));
  }, [ME_LABEL, users]);
  const handleSetUserId = useCallback(
    (userId: OptionKey) => {
      handleSetState({ userId: userId as string });
    },
    [handleSetState]
  );

  useEffect(() => {
    if (deductibleId) {
      handleSetState({ userId: deductibleId });
    }
  }, [deductibleId, handleSetState]);

  useBreadcrumbs(
    [
      {
        key: ROUTES.HOME,
        title: t('Home'),
        value: ROUTES.HOME,
      },
      {
        key: ROUTES.SPENDS_BY_ID(deductibleId, yearId),
        title: t(`Deductible ${currentUser?.fullName}`),
        value: ROUTES.SPENDS_BY_ID(deductibleId, yearId),
      },
    ],
    true
  );

  return (
    <Box direction="column" fill>
      <Box
        pad={{ vertical: 'xxs', horizontal: 'xs' }}
        direction="row"
        round="l"
        align="center"
        justify="between"
      >
        <Box direction="row" align="center">
          <Box width="40px" height="40px" align="center" round="50%" justify="center" background="subgroup">
            <Text color="iconOnColor" weight={700}>
              {abbr}
            </Text>
          </Box>
          <Text margin={{ left: 'xs' }} size="3xl" color="textBody" weight={700}>
            {currentUser?.isMe ? `${currentUser?.fullName}${ME_LABEL}` : currentUser?.fullName}
          </Text>
        </Box>
        {users?.length ? (
          <Box width="200px" margin={{ left: 'spacing8' }}>
            <SelectDropdown
              id="deductible-users"
              testId="deductible-users"
              name="deductibleUsers"
              singleMode
              activeTitle
              options={usersOptions}
              value={selectedUserId}
              onChange={handleSetUserId}
            />
          </Box>
        ) : null}
      </Box>

      <SpendTracker isIndividual defaultHealthPlanYearId={yearId} />
    </Box>
  );
};

export default Deductibles;
